/**
 * InternetVerdict — "The Internet's Verdict" (conversion spec P3, §7).
 *
 * The anti-bounce module: the #1 clickout killer on considered purchases is
 * unresolved doubt — the shopper leaves to search "{model} reddit" and
 * someone else's affiliate link gets the last touch. This module shows the
 * research is already done HERE, then hands peak intent to the payoff CTA.
 *
 * Data: fin_wtis (Layer 3, pip-compute-wtis). P3a renders ONLY aggregates
 * and source names — zero third-party text, so no moderation dependency.
 * The LLM fields (verdict paragraph, pros/cons, owner quotes) render only
 * when the P3b synthesis pass has run AND moderation approved them.
 *
 * Real-or-Hidden Rule 3: null row or too-thin evidence → null (no module).
 */

import type { ReactNode } from 'react'
import { SignedOfferLink } from '@/components/product/signed-offer-link'

export interface WtisRow {
  fin: string
  review_count: number
  rating_count: number
  positive_pct: number | string | null
  weighted_rating: number | string | null
  source_count: number
  editorial_sources: Array<{ domain: string; name: string }> | null
  reddit_threads: number
  subreddits: string[] | null
  video_review_count: number
  verdict: string | null
  pros: string[] | null
  cons: string[] | null
  /** P3b synthesis output: ≤40-word excerpts, subreddit-attributed, no
   * usernames — render only when moderation.approved. */
  reddit_quotes: Array<{ text: string; subreddit: string; context?: string | null }> | null
  /** P3b: 1–2 paragraph summary grounded ONLY in fetched thread text
   * (fin_reddit_threads) — render only when moderation.approved. */
  reddit_summary: string | null
  /** P3b: Reddit has no star ratings — threads read positive or negative.
   * Presented as a sentiment label, never as stars. */
  reddit_sentiment: { label: 'positive' | 'mixed' | 'negative' } | null
  moderation: { approved?: boolean } | null
}

interface InternetVerdictProps {
  wtis: WtisRow | null | undefined
  /** Model phrase for the headline (brand + display model, never a SKU/ASIN). */
  modelLabel: string
  /** Payoff CTA — buybox winner via /r/ redirect (pos=verdict). Fresh price
   *  only. Always renders through SignedOfferLink (HMAC-signed click, item 17;
   *  client-minted cid, FRIDGE-276) — the caller passes the offer, never a
   *  pre-built href, so no unsigned/cid-less anchor can be rendered here. */
  cta?: { retailer: string; priceDisplay: string; offerId: string; productId?: string } | null
  /**
   * LLM-free highlights computed from data already on the page (owner QA
   * 07-06: the counts-only module read as empty). Topics come from
   * fin_review_topic_index; the two quotes are rule-picked from the SAME
   * moderation-gated review rows the reviews module renders (strict mode ⇒
   * display_eligible only), so this adds no new safety surface.
   */
  highlights?: {
    topics: Array<{
      name: string
      count: number
      /** Per-topic sentiment (rated_count-floored upstream detail: chips show
       * the average only when >= 3 matched reviews carried a rating). */
      avgRating?: number | null
      ratedCount?: number
    }>
    /** Single most-helpful positive quote. The negative counterpart was
     * dropped (owner note 07-08 — no negatives surfaced at the top level). */
    praise: { body: string; rating: number } | null
  } | null
  /**
   * Grounded spec facts (capacity, dimensions, ENERGY STAR, current price)
   * built by the caller from real product fields — the former standalone
   * "Expert Verdict" card, folded in here (2026-07-08 reorder) so the page
   * shows ONE consensus module instead of two competing verdict cards.
   * Rendered as a compact "Fit & facts" list. Self-hides when empty.
   */
  facts?: string[]
  /**
   * Owner Q&A (spec §7a) — the review-grounded FAQ (fin_faqs), moved up from the
   * standalone AutoFAQ block so the internet-sourced answers live INSIDE the
   * Verdict, visually distinct from the spec-derived FAQ below. `mentionCount` is
   * the number of owner reviews the answer is grounded in
   * (fin_faqs.source_review_ids.length); self-hides when absent.
   */
  reviewFaqs?: Array<{ question: string; answer: string; mentionCount?: number | null }> | null
  /**
   * Whether a spec FAQ section actually renders further down the page (FAQ
   * rewrite: the data-gated SpecFAQ self-hides below 2 items, so the "see the
   * spec FAQ below" cross-reference must not dangle when it's absent).
   */
  hasSpecFaqBelow?: boolean
  className?: string
}

/**
 * Competitor review publications that must not be promoted on a Fridge.com
 * page (owner ruling FRIDGE-353, 08/17/2026: "should consumer reports and
 * rtings be on our site? i see them on product pages ... i don't think so").
 * fin_wtis.editorial_sources still records them — the aggregation pipeline
 * and its allow-list (lib/pip/editorial-sources.ts) are untouched — but the
 * "Sources reviewed" chips and the "N expert reviews" count are computed
 * from the DISPLAY list, which drops these by domain or by name. Live on
 * 08/17/2026: 84 rows named Consumer Reports and 23 named RTINGS.
 */
export const SUPPRESSED_REVIEW_PUBLICATIONS: ReadonlyArray<{ domain: string; name: string }> = [
  { domain: 'consumerreports.org', name: 'Consumer Reports' },
  { domain: 'rtings.com', name: 'RTINGS' },
]

function isSuppressedPublication(source: { domain?: string | null; name?: string | null }): boolean {
  const domain = String(source.domain ?? '').toLowerCase().replace(/^www\./, '')
  const name = String(source.name ?? '').trim().toLowerCase()
  return SUPPRESSED_REVIEW_PUBLICATIONS.some(
    (s) => domain === s.domain || domain.endsWith(`.${s.domain}`) || name === s.name.toLowerCase(),
  )
}

/**
 * The editorial sources this module may SHOW: the stored list minus the
 * suppressed publications. Exported so the PDP's evidence gate, the headline
 * count and the chips all read one list.
 */
export function displayEditorialSources(
  wtis: WtisRow | null | undefined,
): Array<{ domain: string; name: string }> {
  return (wtis?.editorial_sources ?? []).filter((s) => s && !isSuppressedPublication(s))
}

/**
 * Evidence gate, exported so the PDP can decide BETWEEN this module and the
 * legacy Expert Verdict fallback without re-implementing the threshold.
 * Mirrors the internal `hasEvidence` check exactly. Editorial evidence counts
 * only what the module would actually show (suppressed publications do not
 * open the gate on their own).
 */
export function hasVerdictEvidence(wtis: WtisRow | null | undefined): boolean {
  if (!wtis) return false
  return (
    wtis.review_count >= 5 ||
    displayEditorialSources(wtis).length > 0 ||
    hasShowableRedditEvidence(wtis)
  )
}

/**
 * FRIDGE-665 (owner annotation on FIN-0000820: "the see mention of reddit but
 * where are the reddit reviews?") — Reddit evidence counts ONLY when the
 * module can actually SHOW it: approved synthesis exists. 798 of 1,183 live
 * fin_wtis rows carried a search-result count with ZERO synthesis behind it
 * (fin_reddit_threads holds no fetched content — the fetch and the LLM lane
 * are both blocked on owner-side credentials), and 500 of those opened this
 * gate on the count alone, rendering an empty-promise section. The headline
 * count, the subreddit chips, and this gate all key on the same condition as
 * the verdict-reddit content block, so count and content can never disagree.
 */
export function hasShowableRedditEvidence(wtis: WtisRow | null | undefined): boolean {
  if (!wtis) return false
  const approved = wtis.moderation?.approved === true
  const synthesized =
    wtis.reddit_summary != null || (wtis.reddit_quotes != null && wtis.reddit_quotes.length > 0)
  return approved && synthesized && wtis.reddit_threads > 0
}

/** Show the owner's words in full (owner report 07-08: the cards were
 * clipping mid-sentence with an ellipsis). Only whitespace is normalized. */
function cleanQuote(body: string): string {
  return body.trim().replace(/\s+/g, ' ')
}

function num(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * Info tooltip. The full explanation lives in BOTH a native `title` (on the
 * wrapper AND the SVG's <title>) — which always works on hover with zero
 * dependency on the Tailwind bundle being loaded/fresh — and a styled bubble
 * as progressive enhancement. Owner report 07-08: the CSS-only bubble showed
 * nothing on hover (stale CSS bundle / dev delivery), so native is the floor.
 */
function InfoTooltip({ text }: { text: string }) {
  return (
    <span
      className="group relative inline-flex items-center align-middle ml-1"
      title={text}
    >
      <svg
        role="img"
        aria-label={text}
        tabIndex={0}
        viewBox="0 0 20 20"
        fill="currentColor"
        className="w-3.5 h-3.5 text-gray-600 hover:text-gray-600 cursor-help focus:outline-none focus:ring-1 focus:ring-gray-400 rounded-full"
      >
        <title>{text}</title>
        <path
          fillRule="evenodd"
          d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
      <span
        role="tooltip"
        className="pointer-events-none absolute right-0 bottom-full z-20 mb-1.5 hidden w-64 rounded-md bg-gray-900 px-2.5 py-1.5 text-[11px] font-normal leading-snug tracking-normal normal-case text-left text-white shadow-lg group-hover:block group-focus-within:block"
      >
        {text}
      </span>
    </span>
  )
}

const WEIGHTED_RATING_TOOLTIP =
  'Averaged across every source we found (Amazon, AJ Madison, and other retailers). Each source counts in proportion to how many ratings it has, so a store with thousands of reviews moves the number more than one with only a handful.'

export function InternetVerdict({ wtis, modelLabel, cta, highlights, facts, reviewFaqs, hasSpecFaqBelow = false, className = '' }: InternetVerdictProps) {
  if (!wtis) return null
  const editorial = displayEditorialSources(wtis)
  // Chips only for discussions the page actually shows (FRIDGE-665).
  const subreddits = hasShowableRedditEvidence(wtis) ? wtis.subreddits ?? [] : []
  if (!hasVerdictEvidence(wtis)) return null

  const positivePct = num(wtis.positive_pct)
  const weighted = num(wtis.weighted_rating)
  const llmApproved = wtis.moderation?.approved === true

  // Research-labor headline: quantified diligence, only real counts. Each count
  // is wrapped in the orange accent (spec §7) so the diligence reads at a glance.
  // Item 3 (owner 07/20): every count is a jump-link to the on-page section that
  // holds that evidence — owner reviews → the full Customer Reviews block, Reddit
  // + expert reviews → the "Sources reviewed" block inside this module, video
  // reviews → the Video Reviews block. Always an on-page Fridge.com anchor, never
  // an external link (P3a safety contract: we never link out to a source).
  const partNodes: ReactNode[] = []
  const pushPart = (count: number, singular: string, anchor: string) => {
    if (count > 0) {
      partNodes.push(
        <a
          key={singular}
          href={anchor}
          className="rounded hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-[#ff9951]"
        >
          <span className="text-[#ff9951] underline decoration-[#ff9951]/40 underline-offset-2">{count.toLocaleString()}</span> {singular}{count === 1 ? '' : 's'}
        </a>
      )
    }
  }
  pushPart(wtis.review_count, 'owner review', '#customer-reviews')
  // Reddit is counted only when its synthesis renders below (FRIDGE-665).
  pushPart(hasShowableRedditEvidence(wtis) ? wtis.reddit_threads : 0, 'Reddit discussion', '#verdict-sources')
  pushPart(editorial.length, 'expert review', '#verdict-sources')
  pushPart(wtis.video_review_count, 'video review', '#video-reviews')
  const headlineNodes: ReactNode[] =
    partNodes.length > 1
      ? partNodes.flatMap((n, i) =>
          i === 0 ? [n] : [<span key={`sep-${i}`}>{i === partNodes.length - 1 ? ' and ' : ', '}</span>, n]
        )
      : partNodes

  return (
    <section
      id="internet-verdict"
      className={`mt-8 rounded-xl bg-gray-100 py-6 px-5 md:px-6 ${className}`}
      aria-label="The internet's verdict"
      data-testid="internet-verdict"
    >
      <header className="mb-4">
        <p className="text-[#ff9951] font-bold tracking-widest text-[11px] uppercase mb-1.5">The Internet&rsquo;s Verdict</p>
        <h2 className="text-lg md:text-xl font-black tracking-tight text-gray-900 leading-snug">
          Fridge.com analyzed {headlineNodes} of the {modelLabel}.
        </h2>
      </header>

      {/* Consensus meter — white elevated card with the big headline number
          (spec §7): text-4xl percentage, #ff9951 bar on a gray-100 track. */}
      {positivePct !== null && (
        <div className="mt-4 rounded-lg bg-white p-5 shadow-sm shadow-black/[0.04]" data-testid="verdict-consensus">
          {/* The meter row must WRAP. At 320px the "N% positive" headline plus the
              right-hand "X.X★ weighted · across N,NNN ratings" line cannot share
              one line, and an unwrapped flex row pushed the card 4.2px past the
              viewport (measured on FIN-0000112 at w=320, 08/30 visual sweep).
              gap-x-4 keeps the wide-width spacing identical; gap-y-1 keeps the
              wrapped stack tight. The right span wraps its own parts too, so a
              long ratings count cannot re-open the same overflow. */}
          <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1 mb-3">
            <p className="leading-none">
              <span className="text-4xl font-black tracking-tighter text-gray-900">{positivePct}%</span>
              <span className="ml-2 text-sm font-semibold text-gray-500">positive</span>
            </p>
            <span className="text-xs text-black inline-flex flex-wrap items-center justify-end text-right">
              {weighted !== null && (
                <>
                  {weighted.toFixed(1)}<span className="text-gray-500">★</span> weighted
                  <InfoTooltip text={WEIGHTED_RATING_TOOLTIP} />
                  <span className="mx-1">·</span>
                </>
              )}
              across {Math.max(wtis.rating_count, wtis.review_count).toLocaleString()} ratings
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-[#ff9951]"
              style={{ width: `${Math.min(100, Math.max(0, positivePct))}%` }}
              aria-hidden="true"
            />
          </div>
        </div>
      )}

      {/* How We Score It — methodology card (spec §7b), right after the meter so
          it explains the number just shown. Step 02 reuses WEIGHTED_RATING_TOOLTIP
          so the explanation can't drift from the meter's tooltip. Icon-free;
          identity from the big orange numerals. */}
      <div className="mt-4 rounded-lg bg-white p-5 shadow-sm shadow-black/[0.04]" data-testid="verdict-methodology">
        <header className="mb-4">
          <p className="text-[#0469f0] font-bold tracking-widest text-[11px] uppercase mb-1">How We Score It</p>
        </header>
        <div className="divide-y divide-gray-100">
          <div className="flex gap-4 pb-4">
            <span className="text-2xl font-black tracking-tighter text-[#ff9951] leading-none w-8 shrink-0">01</span>
            <div>
              <p className="text-sm font-bold text-gray-900 mb-1">Gathering the reviews</p>
              <p className="text-sm text-gray-500 leading-relaxed">
                We continuously collect owner reviews and public discussions from major retailers and community forums, de-duplicate them, and set aside anything we can&rsquo;t tie to a verified purchase.
              </p>
            </div>
          </div>
          <div className="flex gap-4 pt-4">
            <span className="text-2xl font-black tracking-tighter text-[#ff9951] leading-none w-8 shrink-0">02</span>
            <div>
              <p className="text-sm font-bold text-gray-900 mb-1">Calculating the average</p>
              <p className="text-sm text-gray-500 leading-relaxed">{WEIGHTED_RATING_TOOLTIP}</p>
            </div>
          </div>
        </div>
        <p className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-600 font-medium">&#10003; Updated regularly · No manufacturer input</p>
      </div>

      {/* What owners talk about — a static summary card (owner note 07-08).
          These were pill-shaped and read as clickable, but a click can't
          surface per-topic Reddit excerpts (no fetched thread text), so they
          are a plain read-only bullet list of the most-discussed aspects with
          their review-derived mention count + sentiment. Data:
          fin_review_topic_index (real counts; avg star only shown at
          >= 3 rated reviews). */}
      {highlights?.topics && highlights.topics.length > 0 && (
        <div className="mt-4 rounded-lg bg-white p-5 shadow-sm shadow-black/[0.04]" data-testid="verdict-topics">
          <p className="text-[11px] uppercase tracking-widest text-[#0469f0] font-bold mb-2.5">What owners talk about most</p>
          <ul className="space-y-2">
            {highlights.topics.map((t) => {
              const showRating =
                typeof t.avgRating === 'number' && Number.isFinite(t.avgRating) && (t.ratedCount ?? 0) >= 3
              return (
                <li key={t.name} className="flex items-baseline gap-2.5 text-sm text-gray-800">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#0469f0]" aria-hidden="true" />
                  <span>
                    <span className="font-medium text-gray-900">{t.name}</span>
                    <span className="text-gray-500"> — mentioned in {t.count.toLocaleString()} review{t.count === 1 ? '' : 's'}</span>
                    {showRating && (
                      <span className={`ml-1 font-semibold ${t.avgRating! >= 4 ? 'text-green-700' : t.avgRating! >= 3 ? 'text-yellow-700' : 'text-orange-700'}`}>
                        · {t.avgRating!.toFixed(1)}★
                      </span>
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {/* What owners love — the single most-helpful positive quote. The
          "main complaint" counterpart was REMOVED (owner note 07-08: no
          negative reviews surfaced at the top level). Critical reviews still
          live in the full Customer Reviews section below (histogram + filters). */}
      {highlights?.praise && (
        <figure className="mt-4 rounded-lg bg-[#ff9951]/[0.06] p-4" data-testid="verdict-quotes">
          <figcaption className="text-[11px] uppercase tracking-widest text-[#ff8533] font-bold mb-1.5 flex items-center gap-1.5">
            What owners say <span className="text-[#ff9951]">{'★'.repeat(Math.round(highlights.praise.rating))}</span>
          </figcaption>
          <blockquote className="text-sm text-gray-800 leading-relaxed">
            &ldquo;{cleanQuote(highlights.praise.body)}&rdquo;
          </blockquote>
        </figure>
      )}

      {/* Owner Q&A (spec §7a) — the review-grounded FAQ (fin_faqs) moved up from
          the standalone AutoFAQ, wrapped in its own blue field so it reads as
          internet-sourced and distinct from the spec FAQ below. Gated on the
          fin_faqs' own >=3 threshold (that pipeline is separately fact-checked +
          ASIN-screened + requires source_review_ids, so it does NOT ride the
          Reddit/P3b `moderation.approved` flag — which is blocked on Reddit OAuth
          and would otherwise hide this already-live, safe content). */}
      {reviewFaqs && reviewFaqs.length >= 3 && (
        <div className="mt-4 rounded-xl bg-[#0469f0]/[0.06] p-4" data-testid="verdict-review-faqs">
          <div className="mb-3 px-1">
            <p className="text-[11px] uppercase tracking-widest text-[#0469f0] font-bold mb-1">Owners Answer</p>
            <p className="text-xs text-gray-500 leading-relaxed">
              The questions shoppers ask, answered from{' '}
              <strong className="text-gray-700 font-semibold">real owner reviews and discussions across the web</strong>{' '}
              — not the manufacturer spec sheet.
              {hasSpecFaqBelow && ' See the spec FAQ below for the official numbers.'}
            </p>
          </div>
          <div className="space-y-2">
            {reviewFaqs.map((f, i) => (
              <details
                key={`${i}-${f.question}`}
                className="group rounded-lg bg-white shadow-sm shadow-black/[0.04] overflow-hidden"
              >
                <summary className="w-full px-4 py-3.5 flex items-start justify-between gap-3 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40">
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-gray-900 group-open:text-[#0469f0] transition-colors">{f.question}</span>
                    {typeof f.mentionCount === 'number' && f.mentionCount > 0 && (
                      <span className="mt-0.5 block text-xs text-gray-600">
                        Based on {f.mentionCount.toLocaleString()} owner mention{f.mentionCount === 1 ? '' : 's'}
                      </span>
                    )}
                  </span>
                  <span className="text-2xl leading-none text-gray-300 group-open:text-[#0469f0] group-open:rotate-45 transition-all duration-300 shrink-0 mt-0.5 select-none" aria-hidden="true">+</span>
                </summary>
                <div className="px-4 pb-4 -mt-0.5">
                  <p className="text-sm text-gray-600 leading-relaxed">{f.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      )}

      {/* Fit & facts — grounded spec facts folded in from the former
          standalone Expert Verdict card (2026-07-08 reorder). Every item is a
          real, checkable product fact (capacity, dimensions, ENERGY STAR,
          live price) assembled by the caller. Self-hides when empty. */}
      {facts && facts.length > 0 && (
        <div className="mt-4 rounded-lg bg-[#0469f0]/[0.04] p-4" data-testid="verdict-facts">
          <p className="text-[11px] uppercase tracking-widest text-[#0469f0] font-bold mb-2.5">Fit &amp; facts</p>
          <ul className="space-y-1.5 text-sm text-gray-700 leading-relaxed list-disc pl-5">
            {facts.map((fact, i) => (
              <li key={i}>{fact}</li>
            ))}
          </ul>
        </div>
      )}

      {/* P3b (LLM + moderation): synthesized verdict + pros/cons + Reddit
          excerpts. Render nothing until moderation approves — never raw
          model output. */}
      {llmApproved && wtis.verdict && (
        <p className="mt-4 text-sm text-gray-800 leading-relaxed max-w-2xl" data-testid="verdict-synthesis">{wtis.verdict}</p>
      )}

      {/* What Reddit says — summary grounded in fetched thread text. Reddit
          carries no star ratings, so the tone reads as a sentiment label
          (positive / mixed / negative), never as stars (owner note 07-06). */}
      {llmApproved && (wtis.reddit_summary || (wtis.reddit_quotes && wtis.reddit_quotes.length > 0)) && (
        <div className="mt-5" data-testid="verdict-reddit">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">What Reddit says</p>
            {wtis.reddit_sentiment?.label && (
              <span
                className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                  wtis.reddit_sentiment.label === 'positive'
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : wtis.reddit_sentiment.label === 'negative'
                      ? 'bg-red-50 border-red-200 text-red-800'
                      : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                }`}
                data-testid="reddit-sentiment"
              >
                {wtis.reddit_sentiment.label === 'positive'
                  ? 'Mostly positive'
                  : wtis.reddit_sentiment.label === 'negative'
                    ? 'Mostly negative'
                    : 'Mixed'}
              </span>
            )}
          </div>
          {wtis.reddit_summary && (
            <div className="space-y-2.5 max-w-2xl" data-testid="verdict-reddit-summary">
              {wtis.reddit_summary
                .split(/\n\s*\n/)
                .filter((p) => p.trim().length > 0)
                .slice(0, 2)
                .map((para, i) => (
                  <p key={i} className="text-sm text-gray-800 leading-relaxed">{para.trim()}</p>
                ))}
            </div>
          )}
          {wtis.reddit_quotes && wtis.reddit_quotes.length > 0 && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="verdict-reddit-quotes">
              {wtis.reddit_quotes.slice(0, 4).map((q, i) => (
                <figure key={i} className="rounded-lg bg-[#ff9951]/[0.06] p-3.5">
                  <blockquote className="text-sm text-gray-800 leading-relaxed">&ldquo;{q.text}&rdquo;</blockquote>
                  <figcaption className="mt-1.5 text-[11px] text-gray-500">
                    {q.subreddit}
                    {q.context ? ` · ${q.context}` : ''}
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>
      )}
      {llmApproved && (wtis.pros?.length || wtis.cons?.length) ? (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
          {wtis.pros?.length ? (
            <ul className="space-y-1.5">
              {wtis.pros.slice(0, 4).map((p, i) => (
                <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-green-600 font-bold">+</span>{p}</li>
              ))}
            </ul>
          ) : null}
          {wtis.cons?.length ? (
            <ul className="space-y-1.5">
              {wtis.cons.slice(0, 4).map((c, i) => (
                <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-orange-500 font-bold">−</span>{c}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {/* Source attributions — names only, never linked, never quoted (P3a
          safety contract; unlinked citation per spec §7.5, owner-confirmed). */}
      {(editorial.length > 0 || subreddits.length > 0) && (
        <div id="verdict-sources" className="mt-4 scroll-mt-28 rounded-lg bg-white p-5 shadow-sm shadow-black/[0.04]" data-testid="verdict-sources">
          <p className="text-[11px] uppercase tracking-widest text-gray-500 font-bold mb-2.5">Sources reviewed</p>
          <ul className="flex flex-wrap gap-1.5">
            {editorial.map((s) => (
              <li key={s.domain} className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
                {s.name}
              </li>
            ))}
            {subreddits.slice(0, 4).map((s) => (
              <li key={s} className="text-xs px-2.5 py-1 rounded-full bg-[#ff9951]/10 text-[#ff8533] font-semibold">
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Payoff CTA — the highest-intent moment on the page. */}
      {cta && (
        <div className="mt-4 rounded-lg bg-[#ff9951]/[0.06] px-4 py-3.5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-800">
            Research done. <span className="font-semibold">{cta.retailer}</span> has the best price today —{' '}
            <span className="font-black">{cta.priceDisplay}</span>
          </p>
          <SignedOfferLink
            offerId={cta.offerId}
            productId={cta.productId}
            pos="verdict"
            target="_blank"
            className="bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] px-5 py-2 rounded-lg font-bold text-xs transition-all shadow-sm active:scale-95"
          >
            View Deal
          </SignedOfferLink>
        </div>
      )}
    </section>
  )
}

export default InternetVerdict
